// dependencies
import { List, Map, fromJS } from "immutable";
import PropTypes from "prop-types";
import React from "react";

// components
import BasicReviewFormCard from "./BasicReviewFormCard";
import ExternalReviewFormCard from "./ExternalReviewFormCard";

// utils
import isPlainObject from "../../utils/isPlainObject";

const ReviewFormCard = (props) => {
	const {
		type,
		form,
		assignment,
		editableForm,
		dateType,
		lockedFields,
		isEditing = false,
		isNewForm = false,
		isDeleted = false,
		onEdit = () => {},
		onCancel = () => {},
		onSave = () => {},
		onDelete = () => {},
		className = "",
	} = props;

	const immutableForm = isPlainObject(form) ? fromJS(form) : form;
	const immutableAssignment = isPlainObject(assignment) ? fromJS(assignment) : assignment;
	const immutableEditableForm = isPlainObject(editableForm) ? fromJS(editableForm) : editableForm;
	const immutableLockedFields = Array.isArray(lockedFields) ? fromJS(lockedFields) : lockedFields;

	switch (type) {
		case "external": {
			return (
				<ExternalReviewFormCard
					isEditing={isEditing}
					isDeleted={isDeleted}
					lockedFields={immutableLockedFields}
					editableForm={editableForm}
					onEdit={onEdit}
					onCancel={onCancel}
					onSave={onSave}
					onDelete={onDelete}
					isNewForm={isNewForm}
					className={className}
					name={immutableForm ? immutableForm.get("name") : ""}
					authorName={immutableAssignment.get("authorName")}
					authorEmail={immutableAssignment.get("authorEmail")}
					emailMessage={immutableAssignment.get("emailMessage")}
					visibleToSubject={immutableAssignment.get("visibleToSubject")}
					redacted={immutableAssignment.get("redacted")}
					editableForm={immutableEditableForm}
					lockedFields={immutableLockedFields}
				/>
			);
		}

		case "basic": {
			return (
				<BasicReviewFormCard
					{...props}
					name={immutableForm ? immutableForm.get("name") : ""}
					authors={immutableAssignment.get("authors")}
					signers={immutableAssignment.get("signers")}
					redacted={immutableAssignment.get("redacted")}
					authorsDueDate={
						immutableAssignment.get("authorsDueDate") || immutableAssignment.get("daysToCompleteAuthors")
					}
					signersDueDate={
						immutableAssignment.get("signersDueDate") || immutableAssignment.get("daysToCompleteSigners")
					}
					visibleToSubject={immutableAssignment.get("visibleToSubject")}
					editableForm={immutableEditableForm}
					dateType={dateType}
					lockedFields={immutableLockedFields}
				/>
			);
		}

		default: {
			return null;
		}
	}
};

ReviewFormCard.propTypes = {
	type: PropTypes.string.isRequired,
	form: PropTypes.oneOfType([PropTypes.instanceOf(Map), PropTypes.object]),
	assignment: PropTypes.oneOfType([PropTypes.instanceOf(Map), PropTypes.object]).isRequired,
	/** Handle clicking the edit action */
	onEdit: PropTypes.func,
	/** Handle clicking the delete action */
	onDelete: PropTypes.func,
	/** Handle clicking the cancel action */
	onCancel: PropTypes.func,
	/** Handle clicking the save action */
	onSave: PropTypes.func,
	/** Will put the card in edit mode if true */
	isEditing: PropTypes.bool,
	/** Additional class */
	className: PropTypes.string,
	/** the selected form for the dropdown. If this value is `undefined`, then
	 * the dropdown is not displayed. This can also be null if nothing is
	 * selected. */
	editableForm: PropTypes.shape({
		id: PropTypes.string,
		name: PropTypes.string,
	}),
	dateType: PropTypes.string,
	authorRequired: PropTypes.bool,
	lockedFields: PropTypes.oneOfType([PropTypes.instanceOf(List), PropTypes.array]),
	/** Indicates if a form is newly added and not yet saved */
	isNewForm: PropTypes.bool,
	isDeleted: PropTypes.bool,
};

export default ReviewFormCard;
